/**
 * Read-only renderer for an association row that a proposal changes.
 *
 * Canonical companion to `PropertyDiffValue`: where that renders a property's
 * before/after, this renders an association's. Both are used by the record
 * overview so a projected record reads the same whichever kind of field
 * changed.
 *
 * Two shapes, chosen by what the proposal actually states:
 *
 *   swap  — exactly one target removed and one added, with nothing else on the
 *           row. That is unambiguously a replacement, so it renders as
 *           `old → new`, matching the property diff directly above it.
 *   set   — everything else. Badges render together, each marked with its own
 *           fate (added tinted, removed struck through, untouched plain).
 *
 * The set shape is what one-to-many rows need: "added two, removed one, kept
 * three" has no arrow form, and forcing one would have to invent a pairing the
 * proposal never expressed. Reserving the arrow for the genuine 1↔1 case keeps
 * it honest — a record can legitimately hold several associations of the same
 * type, so an addition alone never implies the existing one goes away.
 */

import { ArrowRight } from '@untitledui/icons';

import type { AssociationChange } from '@/lib/crm/record/buildProjectedObject';

import { EntityBadgeByIds } from '../../badges/EntityBadgeByIds';
import type { EntityBadgeChangeMarker } from '../../badges/types';

export interface AssociationDiffValueProps {
  /** Target entity ids on the row, in render order. */
  entityIds: readonly string[];
  /** What the proposal does to each target, keyed by entity id. */
  changes: Readonly<Record<string, AssociationChange>>;
  orgId: number;
}

const EMPTY_MARKERS: Readonly<Record<string, EntityBadgeChangeMarker>> = {};

export function AssociationDiffValue({
  entityIds,
  changes,
  orgId,
}: AssociationDiffValueProps) {
  const removed = entityIds.filter(id => changes[id] === 'removed');
  const added = entityIds.filter(id => changes[id] === 'added');
  const untouched = entityIds.filter(id => !changes[id]);

  const isSwap =
    removed.length === 1 && added.length === 1 && untouched.length === 0;

  if (isSwap) {
    return (
      <span className='flex min-w-0 flex-wrap items-center gap-1'>
        <EntityBadgeByIds
          entityIds={removed}
          orgId={orgId}
          variant='xs'
          changeMarkers={changes}
        />
        <ArrowRight
          className='size-3 shrink-0 text-fg-quaternary'
          aria-hidden='true'
        />
        <EntityBadgeByIds
          entityIds={added}
          orgId={orgId}
          variant='xs'
          changeMarkers={changes}
        />
      </span>
    );
  }

  // Removed first so the row reads as "what goes, what stays, what arrives"
  // rather than burying a removal among unchanged badges.
  const ordered = [...removed, ...untouched, ...added];
  return (
    <EntityBadgeByIds
      entityIds={ordered}
      orgId={orgId}
      variant='xs'
      changeMarkers={Object.keys(changes).length > 0 ? changes : EMPTY_MARKERS}
    />
  );
}
